/**
 * ActionBarHudCluster.tsx
 *
 * Renders all visible (`shown !== false`) action bars on the HUD at their
 * configured spots (`main`, `above`, `right`, `left`; D1), above chat, and
 * scaled in compact mode on phones (Stage 1). During session bars (`peaceful`,
 * `race`), extra bars hide automatically (D3).
 */

import React, { useEffect, useRef } from 'react';
import type { Helix2000Character } from '../../../../shared/CharacterTypes';
import type { ActionBarAssignment } from '../../../../shared/types/Input';
import { mainBar, type BarState, type SlotRef } from '../../../../shared/hotbar/barLayout';
import { HUD_Z_INDEX } from '../../../../config/ui/hudDefaults.config';
import { BuffBar } from '../../HUD/BuffBar';
import {
  actionBarCompactContainerStyle,
  actionBarContainerStyle,
} from '../../HUD/HUD.styles';
import type { HudConcreteStyle } from '../../HUD/useHudTomeVibe';
import { ACCENT, COLORS, FONTS, FONT_SIZE, FONT_WEIGHT, INK, RADII, SHADOW, SPACE } from '../../theme';
import { ActionBar, type ActionBarLayoutEdit } from './index';
import { quickKeybindStore, useQuickKeybindSnapshot } from './quickKeybindStore';

export interface ActionBarHudClusterProps {
  player: Helix2000Character;
  compact: {
    isCompact: boolean;
    actionBarScale: number;
    actionBarBottomInset: number;
  };
  hudStyle?: HudConcreteStyle;
  onAction?: (action: string, data?: any) => void;
  buildActionBarUpdate: (
    slotOrRef: number | SlotRef,
    assignment: ActionBarAssignment | null,
  ) => { slotIndex: number; bar?: number; page?: number; abilityId?: string; itemId?: string };
}

export const ActionBarHudCluster: React.FC<ActionBarHudClusterProps> = ({
  player,
  compact,
  hudStyle,
  onAction,
  buildActionBarUpdate,
}) => {
  const actionBar = player.actionBar || { slots: [], activeBar: 1 };
  const abilities = player.abilities || [];
  const sessionLocked = actionBar.editable === false && actionBar.mode !== 'base';
  const rawBars: ReadonlyArray<BarState & { shown?: boolean }> = actionBar.layout?.bars ?? [mainBar()];
  const { quickKeybindActive, hudHoveredSlot, statusMessage, statusTone } = useQuickKeybindSnapshot();
  const pendingModifierRef = useRef<string | null>(null);

  useEffect(() => {
    if (!quickKeybindActive) {
      pendingModifierRef.current = null;
      return;
    }
    const onKeyDown = (e: KeyboardEvent) => {
      quickKeybindStore.handleKeyDownInQuickMode(e, pendingModifierRef);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      quickKeybindStore.handleKeyUpInQuickMode(e, pendingModifierRef);
    };
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('keyup', onKeyUp, true);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('keyup', onKeyUp, true);
    };
  }, [quickKeybindActive]);

  const shownBars = rawBars.filter((bar) => {
    if (bar.id === 1) return true;
    if (sessionLocked) return false;
    return bar.shown !== false && bar.visible !== false;
  });

  const mainBarState = shownBars.find((b) => b.id === 1) ?? mainBar();
  const aboveBars = shownBars
    .filter((b) => b.id !== 1 && b.spot === 'above')
    .sort((a, b) => b.id - a.id);
  const rightBars = shownBars
    .filter((b) => b.id !== 1 && b.spot === 'right')
    .sort((a, b) => a.id - b.id);
  const leftBars = shownBars
    .filter((b) => b.id !== 1 && b.spot === 'left')
    .sort((a, b) => a.id - b.id);

  const handleAbilityUse = (abilityId: string) => onAction?.('useAbility', abilityId);
  const handleItemUse = (itemId: string) => onAction?.('useItem', { itemId });
  const handleSlotUpdate = (slotIndex: number, assignment: ActionBarAssignment | null) =>
    onAction?.('updateActionBar', buildActionBarUpdate(slotIndex, assignment));
  const handleSlotRefUpdate = (ref: SlotRef, assignment: ActionBarAssignment | null) =>
    onAction?.('updateActionBar', buildActionBarUpdate(ref, assignment));
  const handleSlotMove = (from: number | SlotRef, to: number | SlotRef) =>
    onAction?.('moveActionBarSlot', { from, to });
  const handleLayoutEdit = (edit: ActionBarLayoutEdit) =>
    onAction?.('editActionBarLayout', edit);

  const renderBar = (bar: BarState, orientation: 'horizontal' | 'vertical', style?: React.CSSProperties) => (
    <ActionBar
      key={bar.id}
      player={player}
      actionBar={actionBar}
      abilities={abilities}
      barId={bar.id}
      barState={bar}
      orientation={orientation}
      style={style}
      onAbilityUse={handleAbilityUse}
      onItemUse={handleItemUse}
      onSlotUpdate={handleSlotUpdate}
      onSlotRefUpdate={handleSlotRefUpdate}
      onSlotMove={handleSlotMove}
      onLayoutEdit={handleLayoutEdit}
    />
  );

  return (
    <>
      {quickKeybindActive && (
        <div
          data-testid="quick-keybind-banner"
          role="status"
          aria-live="polite"
          style={quickKeybindBannerStyle}
        >
          <div style={quickKeybindHeaderRowStyle}>
            <span style={quickKeybindTitleStyle}>⌨️ Quick Keybind Mode (/kb)</span>
            <span
              data-testid="quick-keybind-target"
              style={quickKeybindTargetBadgeStyle}
            >
              {hudHoveredSlot
                ? `Target: Bar ${hudHoveredSlot.bar} · Slot ${hudHoveredSlot.slot}`
                : 'Hover an action bar slot'}
            </span>
            <button
              type="button"
              data-testid="quick-keybind-done"
              onClick={() => quickKeybindStore.toggleQuickKeybindMode(false)}
              style={quickKeybindDoneBtnStyle}
            >
              Done (Esc)
            </button>
          </div>
          <div
            data-testid="quick-keybind-status"
            style={{
              ...quickKeybindSubStyle,
              color:
                statusTone === 'error'
                  ? COLORS.danger
                  : statusTone === 'success'
                    ? '#70ffa0'
                    : COLORS.textSecondary,
            }}
          >
            {statusMessage ??
              'Hover any slot and press a key (or Shift/Ctrl/Alt + key) to bind. Press Backspace to clear.'}
          </div>
        </div>
      )}

      <div
        data-testid="hud-action-bar-band"
        data-hud-style={hudStyle}
        style={compact.isCompact
          ? actionBarCompactContainerStyle(compact.actionBarScale, compact.actionBarBottomInset)
          : actionBarContainerStyle}
      >
        {/* Buff/debuff row — stacks just above the hotbar cluster; self-hides
            when no effects are active (HUD-restyle Lane B.6). */}
        <BuffBar playerId={player.id} />
        {aboveBars.map((bar) => renderBar(bar, 'horizontal', { marginBottom: 6 }))}
        {renderBar(mainBarState, 'horizontal')}
      </div>

      {rightBars.length > 0 && (
        <div
          data-testid="hud-action-bar-right-dock"
          data-hud-style={hudStyle}
          style={edgeDockStyle('right', compact.isCompact ? compact.actionBarScale : 1)}
        >
          {rightBars.map((bar) => renderBar(bar, 'vertical'))}
        </div>
      )}

      {leftBars.length > 0 && (
        <div
          data-testid="hud-action-bar-left-dock"
          data-hud-style={hudStyle}
          style={edgeDockStyle('left', compact.isCompact ? compact.actionBarScale : 1)}
        >
          {leftBars.map((bar) => renderBar(bar, 'vertical'))}
        </div>
      )}
    </>
  );
};

function edgeDockStyle(side: 'left' | 'right', scale: number): React.CSSProperties {
  return {
    position: 'absolute',
    [side]: 16,
    top: '50%',
    transform: scale < 1 ? `translateY(-50%) scale(${scale})` : 'translateY(-50%)',
    transformOrigin: `${side} center`,
    display: 'flex',
    flexDirection: 'row',
    gap: 6,
    zIndex: HUD_Z_INDEX.actionbar,
    pointerEvents: 'auto',
  };
}

const quickKeybindBannerStyle: React.CSSProperties = {
  position: 'fixed',
  top: 72,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: HUD_Z_INDEX.actionbar + 25,
  minWidth: 380,
  maxWidth: '90vw',
  padding: `${SPACE.sm}px ${SPACE.md}px`,
  borderRadius: RADII.lg,
  background: `linear-gradient(180deg, ${INK.hi}, ${INK.base} 60%, ${INK.lo})`,
  border: '1.5px solid #ffd86b',
  boxShadow: `${SHADOW.md}, 0 0 18px rgba(255, 216, 107, 0.35)`,
  color: COLORS.textPrimary,
  fontFamily: FONTS.ui,
  pointerEvents: 'auto',
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
};

const quickKeybindHeaderRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: SPACE.sm,
};

const quickKeybindTitleStyle: React.CSSProperties = {
  fontFamily: FONTS.display,
  fontSize: FONT_SIZE.sm,
  fontWeight: FONT_WEIGHT.bold,
  color: '#ffd86b',
};

const quickKeybindTargetBadgeStyle: React.CSSProperties = {
  padding: '2px 8px',
  borderRadius: RADII.sm,
  background: 'rgba(255, 216, 107, 0.16)',
  border: `1px solid ${ACCENT.brass}`,
  color: COLORS.textPrimary,
  fontSize: FONT_SIZE.xs,
  fontWeight: FONT_WEIGHT.semibold,
};

const quickKeybindDoneBtnStyle: React.CSSProperties = {
  padding: '3px 10px',
  borderRadius: RADII.sm,
  background: 'rgba(255, 216, 107, 0.22)',
  border: '1px solid #ffd86b',
  color: '#ffd86b',
  fontSize: FONT_SIZE.xs,
  fontWeight: FONT_WEIGHT.bold,
  cursor: 'pointer',
};

const quickKeybindSubStyle: React.CSSProperties = {
  fontSize: FONT_SIZE.xs,
  lineHeight: 1.35,
};
